import { useCallback } from 'react';

import type { Stripe, StripeError } from '@stripe/stripe-js';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useLocale } from 'src/app/shared/hooks/l10n';
import type { PaymentMethodSaveOptions } from 'src/app/shared/utils/payments';
import {
	confirmStripeCardSetup,
	saveStripePaymentMethod,
	setupStripePaymentMethod,
} from 'src/app/shared/utils/payments';

import { useLoadStripe } from '../../../../../../hooks';

type SavePaymentMethodParams = {
	stripe: Stripe;
	token: string;
	email: string;
	countryCode: string;
	currency: string;
	paymentMethodSaveOptions: PaymentMethodSaveOptions;
};

type SavePaymentMethodResult = { success: true; token: string } | { success: false; error: StripeError };

type Result = {
	savePaymentMethod: (params: SavePaymentMethodParams) => Promise<SavePaymentMethodResult>;
};

export function useSavePaymentMethod(): Result {
	const locale = useLocale();
	const utilityContext = useUtilityContext();
	const loadStripeInstance = useLoadStripe();
	const track = useTracking();

	const savePaymentMethod = useCallback(
		async (params: SavePaymentMethodParams): Promise<SavePaymentMethodResult> => {
			const { stripe, token, email, countryCode, currency, paymentMethodSaveOptions } = params;

			if (!paymentMethodSaveOptions.shouldSavePaymentMethod) {
				return { success: true, token };
			}

			const { accountId, clientSecret, paymentMethodId } = await setupStripePaymentMethod(
				{ id: token },
				paymentMethodSaveOptions.usage,
				utilityContext,
				// Pass countryCode and currency to retrieve correct accountId from gateway router
				'creditCard',
				countryCode,
				currency,
			);

			// paymentMethodId is null when payment method is setup on platform account
			const effectiveToken = paymentMethodId ?? token;

			const stripeObj = await loadStripeInstance(stripe, accountId);

			const confirmError = await confirmStripeCardSetup(stripeObj, { id: effectiveToken }, clientSecret, track);
			if (confirmError) {
				return { success: false, error: confirmError };
			}

			await saveStripePaymentMethod({
				token: effectiveToken,
				email,
				usage: paymentMethodSaveOptions.usage,
				utilityContext,
				paymentType: 'creditCard',
				accountId: accountId ?? undefined,
				locale,
				countryCode,
			});

			return { success: true, token: effectiveToken };
		},
		[locale, utilityContext, loadStripeInstance, track],
	);

	return { savePaymentMethod };
}
